import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Grid3x3, X, FileText } from "lucide-react";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { listFunds } from "@/lib/api/funds";
import { listProvisionsByFund, type ProvisionRow } from "@/lib/api/provisions";
import { listLps } from "@/lib/api/lps";
import { listCommitmentsByFund, commitmentMap } from "@/lib/api/commitments";
import { CATEGORY_LABELS, type ProvisionCategory } from "@argus/engine";
import { Card, Badge, EmptyState, Spinner, ConfidenceChip } from "@/components/ui/primitives";
import { fmtUsd, fmtPct } from "@/lib/format";

// Curated, demo-meaningful column set (subset of the 23-category taxonomy).
const COLUMNS: ProvisionCategory[] = [
  "mgmt_fee_discount",
  "carry_modification",
  "expense_cap",
  "mfn",
  "co_invest",
  "transparency",
  "key_person",
  "reporting_freq",
  "transfer",
  "esg",
];

const SHORT_COL: Partial<Record<ProvisionCategory, string>> = {
  mgmt_fee_discount: "Mgmt fee",
  carry_modification: "Carry",
  expense_cap: "Exp. cap",
  mfn: "MFN",
  co_invest: "Co-invest",
  transparency: "Transparency",
  key_person: "Key person",
  reporting_freq: "Reporting",
  transfer: "Transfer",
  esg: "ESG",
};

function cellValue(p: ProvisionRow): string {
  const v = p.structured_value;
  if (v?.fee_pct !== undefined) return fmtPct(v.fee_pct);
  if (v?.carry_pct !== undefined) return fmtPct(v.carry_pct);
  if (v?.cap_usd !== undefined) return fmtUsd(v.cap_usd);
  if (p.category === "mfn" && v?.mfn_scope) return v.mfn_scope.replace(/_/g, " ");
  return "✓";
}

function MatrixInner() {
  const tenant = useTenant();
  const { data: funds } = useQuery({
    queryKey: ["funds", tenant.id],
    queryFn: () => listFunds(tenant.id),
  });
  const [fundId, setFundId] = useState("");
  const activeFundId = fundId !== "" ? fundId : (funds?.[0]?.id ?? "");

  const { data, isLoading } = useQuery({
    queryKey: ["matrix", tenant.id, activeFundId],
    queryFn: async () => {
      const [provisions, lps, commitments] = await Promise.all([
        listProvisionsByFund(tenant.id, activeFundId, { activeOnly: true }),
        listLps(tenant.id),
        listCommitmentsByFund(tenant.id, activeFundId),
      ]);
      return { provisions, lps, commitments: commitmentMap(commitments) };
    },
    enabled: !!activeFundId,
  });

  const [selected, setSelected] = useState<{
    lpName: string;
    category: ProvisionCategory;
    rows: ProvisionRow[];
  } | null>(null);

  // Build LP rows: only LPs that have a commitment in this fund, ordered by size.
  const grid = useMemo(() => {
    if (!data) return [];
    const lpsById = new Map(data.lps.map((l) => [l.id, l]));
    const byLp = new Map<string, Map<ProvisionCategory, ProvisionRow[]>>();
    for (const p of data.provisions) {
      if (!p.lp_id) continue;
      const m = byLp.get(p.lp_id) ?? new Map<ProvisionCategory, ProvisionRow[]>();
      const arr = m.get(p.category) ?? [];
      arr.push(p);
      m.set(p.category, arr);
      byLp.set(p.lp_id, m);
    }
    return Object.keys(data.commitments)
      .sort((a, b) => (data.commitments[b] ?? 0) - (data.commitments[a] ?? 0))
      .map((lpId) => ({
        lpId,
        name: lpsById.get(lpId)?.short_name ?? lpsById.get(lpId)?.legal_name ?? "LP",
        commitment: data.commitments[lpId],
        cells: byLp.get(lpId) ?? new Map<ProvisionCategory, ProvisionRow[]>(),
      }));
  }, [data]);

  return (
    <>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-neutral-50">
            <Grid3x3 className="text-brand-400 size-5" /> LP comparison matrix
          </h1>
          <p className="max-w-2xl text-sm text-neutral-400">
            Every negotiated term across the book, one row per LP. Click any cell to read the
            verbatim provision and jump to its source document.
          </p>
        </div>
        {funds && funds.length > 0 && (
          <select
            className="input w-56"
            value={activeFundId}
            onChange={(e) => setFundId(e.target.value)}
          >
            {funds.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        )}
      </header>

      {isLoading ? (
        <Card className="flex items-center gap-2 p-10 text-sm text-neutral-500">
          <Spinner /> Building matrix…
        </Card>
      ) : grid.length === 0 ? (
        <EmptyState
          icon={<Grid3x3 className="size-8" />}
          title="No provisions to compare yet"
          description="Upload and extract side letters for this fund — the matrix fills in automatically as terms are confirmed."
        />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 border-b border-neutral-800 bg-neutral-900/80 px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wide text-neutral-500 backdrop-blur">
                  Limited partner
                </th>
                {COLUMNS.map((c) => (
                  <th
                    key={c}
                    className="border-b border-neutral-800 bg-neutral-900/40 px-3 py-2.5 text-center text-[11px] font-medium text-neutral-400"
                    title={CATEGORY_LABELS[c]}
                  >
                    {SHORT_COL[c] ?? CATEGORY_LABELS[c]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.map((row) => (
                <tr key={row.lpId} className="group">
                  <td className="sticky left-0 z-10 border-b border-neutral-900 bg-neutral-950/90 px-4 py-2.5 backdrop-blur group-hover:bg-neutral-900/60">
                    <div className="font-medium text-neutral-100">{row.name}</div>
                    <div className="text-[11px] tabular-nums text-neutral-500">
                      {fmtUsd(row.commitment)}
                    </div>
                  </td>
                  {COLUMNS.map((c) => {
                    const rows = row.cells.get(c);
                    const first = rows?.[0];
                    const economic =
                      c === "mgmt_fee_discount" ||
                      c === "carry_modification" ||
                      c === "expense_cap";
                    return (
                      <td key={c} className="border-b border-neutral-900 px-3 py-2.5 text-center">
                        {rows && first ? (
                          <button
                            onClick={() => setSelected({ lpName: row.name, category: c, rows })}
                            className={`min-w-[3rem] rounded px-2 py-0.5 text-xs font-medium tabular-nums transition ${
                              economic
                                ? "bg-brand-950/50 text-brand-300 hover:bg-brand-900/50"
                                : "bg-neutral-800/60 text-neutral-300 hover:bg-neutral-700/60"
                            }`}
                          >
                            {cellValue(first)}
                          </button>
                        ) : (
                          <span className="text-neutral-700">·</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <button
            type="button"
            aria-label="Close detail"
            tabIndex={-1}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setSelected(null)}
          />
          <div className="relative z-10 flex h-full w-full max-w-md flex-col border-l border-neutral-800 bg-neutral-950 shadow-2xl">
            <div className="flex items-start justify-between border-b border-neutral-800 p-4">
              <div>
                <div className="text-xs uppercase tracking-wide text-neutral-500">
                  {selected.lpName}
                </div>
                <div className="text-base font-semibold text-neutral-50">
                  {CATEGORY_LABELS[selected.category]}
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="rounded p-1 text-neutral-500 hover:bg-neutral-800 hover:text-neutral-200"
              >
                <X className="size-4" />
              </button>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto p-4">
              {selected.rows.map((p) => (
                <div
                  key={p.id}
                  className="space-y-2 rounded-lg border border-neutral-800 bg-neutral-900/40 p-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    {p.confidence !== null && <ConfidenceChip value={p.confidence} />}
                    <Badge
                      tone={
                        p.review_status === "confirmed" || p.review_status === "edited"
                          ? "clear"
                          : "neutral"
                      }
                    >
                      {p.review_status}
                    </Badge>
                  </div>
                  {p.summary && <p className="text-sm text-neutral-200">{p.summary}</p>}
                  <blockquote className="border-l-2 border-neutral-700 pl-3 text-xs italic leading-relaxed text-neutral-400">
                    {p.raw_text}
                  </blockquote>
                  <Link
                    to="/t/$slug/documents/$documentId"
                    params={{ slug: tenant.slug, documentId: p.document_id }}
                    className="text-brand-400 hover:text-brand-300 inline-flex items-center gap-1.5 text-[11px]"
                  >
                    <FileText className="size-3" /> Open source document
                    {p.page_number ? ` · p.${p.page_number}` : ""}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function MatrixPage() {
  return (
    <TenantLayout>
      <MatrixInner />
    </TenantLayout>
  );
}
